import type { UppFloorPlanTable } from '../plan/floor-plan-element';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';
import type { UppKonvaAttention } from '../../model/upp-konva-types';
import { addFloorPlanHitRect, floorPlanInnerStrokeAttrs } from './paint';

/** v3 `table.js` chair / margin sizes in cm. */
const TABLE_CM = {
  chairWidth: 33,
  chairHeight: 33,
  chairMargin: 20,
} as const;

function tableAttentionPaint(
  theme: UppFloorPlanTheme,
  attention: boolean,
  tone: UppKonvaAttention,
): {
  outerStroke: string;
  chairInner: string;
  tabletopFill: string;
} {
  if (!attention) {
    return {
      outerStroke: theme.elementStroke,
      chairInner: theme.elementFill,
      tabletopFill: theme.elementFill,
    };
  }
  if (tone === 'SUCCESS') {
    return {
      outerStroke: theme.tableAttentionSuccessStroke,
      chairInner: theme.tableAttentionSuccessChairInner,
      tabletopFill: theme.tableAttentionSuccessFill,
    };
  }
  if (tone === 'DANGER') {
    return {
      outerStroke: theme.tableAttentionDangerStroke,
      chairInner: theme.tableAttentionDangerChairInner,
      tabletopFill: theme.tableAttentionDangerFill,
    };
  }
  return {
    outerStroke: theme.tableAttentionStroke,
    chairInner: theme.tableAttentionChairInner,
    tabletopFill: theme.tableAttentionFill,
  };
}

/** Max chairs that fit along one edge (same formula as v3 `table.js`). */
export function maxChairsAlongTableEdge(edgeLenPx: number, cw: number, margin: number): number {
  const itemW = cw + margin;
  return Math.max(0, Math.ceil((edgeLenPx - margin) / itemW));
}

/**
 * Chairs per side [top, right, bottom, left] in local table coords.
 * Square (≈ equal w/h): with ≥4 chairs, one per side first, then extras on sides with spare capacity.
 * Rectangle with ≥4: proportional to side length (top/bottom ∝ w, left/right ∝ h), then remainder on longer edges.
 * Fewer than 4: round-robin like v3.
 */
/**
 * Clockwise seat-fill rank for round/ellipse tables: draw index `i` is at angle `i·2π/n`;
 * rank `0` is the northern seat, then clockwise.
 */
export function roundTableCwRankByDrawIndex(chairCount: number): number[] {
  const n = Math.max(1, Math.trunc(chairCount));
  const items: { i: number; key: number }[] = [];
  for (let i = 0; i < n; i++) {
    const phi = (i * 2 * Math.PI) / n;
    const key = (phi + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI);
    items.push({ i, key });
  }
  items.sort((a, b) => a.key - b.key);
  const rank = new Array<number>(n);
  for (let r = 0; r < n; r++) {
    const it = items[r];
    if (it) {
      rank[it.i] = r;
    }
  }
  return rank;
}

/**
 * For rectangular tables, same chair loop order as painting: top (L→R), right (T→B), bottom (L→R), left (T→B).
 * Returns `rank[drawIndex]` = clockwise distance from top-left walk (0 = first filled seat).
 */
export function rectTableCwRankByDrawIndexPerSide(numChairs: [number, number, number, number]): number[] {
  const n = numChairs;
  const total = n[0] + n[1] + n[2] + n[3];
  const cwSeq: number[] = [];
  for (let c = 0; c < n[0]; c++) {
    cwSeq.push(c);
  }
  const r0 = n[0];
  for (let c = 0; c < n[1]; c++) {
    cwSeq.push(r0 + c);
  }
  const r1 = r0 + n[1];
  for (let c = n[2] - 1; c >= 0; c--) {
    cwSeq.push(r1 + c);
  }
  const r2 = r1 + n[2];
  for (let c = n[3] - 1; c >= 0; c--) {
    cwSeq.push(r2 + c);
  }
  const rankByDraw = new Array<number>(total);
  for (let k = 0; k < cwSeq.length; k++) {
    const di = cwSeq[k];
    if (di !== undefined) {
      rankByDraw[di] = k;
    }
  }
  return rankByDraw;
}

export function allocateChairCountsPerSide(
  w: number,
  h: number,
  chairCount: number,
  cw: number,
  margin: number,
): [number, number, number, number] {
  const cap: [number, number, number, number] = [
    maxChairsAlongTableEdge(w, cw, margin),
    maxChairsAlongTableEdge(h, cw, margin),
    maxChairsAlongTableEdge(w, cw, margin),
    maxChairsAlongTableEdge(h, cw, margin),
  ];
  const num: [number, number, number, number] = [0, 0, 0, 0];
  if (chairCount <= 0) {
    return num;
  }

  const totalCap = cap[0] + cap[1] + cap[2] + cap[3];
  if (totalCap === 0) {
    return num;
  }

  const target = Math.min(chairCount, totalCap);
  const isSquare = Math.abs(w - h) <= Math.max(w, h) * 0.02;

  if (target < 4) {
    let extra = target;
    let side = 0;
    while (extra > 0) {
      let gap = false;
      for (let i = 0; i < 4; i++) {
        if (cap[i] > num[i]) {
          gap = true;
          break;
        }
      }
      if (!gap) {
        break;
      }
      if (num[side] < cap[side]) {
        num[side] += 1;
        extra -= 1;
      }
      side = (side + 1) % 4;
    }
    return num;
  }

  if (isSquare) {
    for (let i = 0; i < 4; i++) {
      if (cap[i] > 0) {
        num[i] = 1;
      }
    }
    let rem = target - (num[0] + num[1] + num[2] + num[3]);
    while (rem > 0) {
      let best = -1;
      let bestRoom = -1;
      for (let i = 0; i < 4; i++) {
        const room = cap[i] - num[i];
        if (room > bestRoom) {
          bestRoom = room;
          best = i;
        }
      }
      if (best < 0 || bestRoom <= 0) {
        break;
      }
      num[best] += 1;
      rem -= 1;
    }
    return num;
  }

  const perim = 2 * (w + h);
  const weights = [w, h, w, h];
  const raw = weights.map((wt) => (target * wt) / perim);
  for (let i = 0; i < 4; i++) {
    num[i] = Math.min(cap[i], Math.floor(raw[i]));
  }
  const used = num[0] + num[1] + num[2] + num[3];
  let rem = target - used;
  while (rem > 0) {
    let best = -1;
    let bestWeight = -1;
    let bestRoom = 0;
    for (let i = 0; i < 4; i++) {
      const room = cap[i] - num[i];
      if (room <= 0) {
        continue;
      }
      const wt = weights[i];
      if (best < 0 || wt > bestWeight || (wt === bestWeight && room > bestRoom)) {
        best = i;
        bestWeight = wt;
        bestRoom = room;
      }
    }
    if (best < 0) {
      break;
    }
    num[best] += 1;
    rem -= 1;
  }
  return num;
}

function drawSquareChair(
  K: any,
  group: any,
  angleDeg: number,
  cx: number,
  cy: number,
  cw: number,
  ch: number,
  outerStroke: string,
  innerStroke: string,
  strokeWidth: number,
  seatFilled: boolean,
): void {
  const swap = angleDeg % 180 !== 0;
  const rw = swap ? ch : cw;
  const rh = swap ? cw : ch;
  const iw = rw - 2;
  const ih = rh - 2;
  const innerFill = seatFilled ? innerStroke : 'rgba(0,0,0,0)';
  // Konva: x,y are the position of the transform origin; with offset = half-size, origin is the chair center (like v3 translate(cx,cy)).
  group.add(
    new K.Rect({
      x: cx,
      y: cy,
      width: rw,
      height: rh,
      fill: 'rgba(0,0,0,0)',
      rotation: angleDeg,
      offsetX: rw / 2,
      offsetY: rh / 2,
      stroke: outerStroke,
      strokeWidth,
      listening: false,
    }),
  );
  group.add(
    new K.Rect({
      x: cx,
      y: cy,
      width: iw,
      height: ih,
      fill: innerFill,
      rotation: angleDeg,
      offsetX: iw / 2,
      offsetY: ih / 2,
      stroke: innerStroke,
      strokeWidth,
      listening: false,
    }),
  );
}

/**
 * Dining table with chairs (v3-style geometry), local coords with table top-left at (tx, ty).
 * Chairs first, table on top so the tabletop covers the half of each chair that sits under the edge (same as v3 draw order).
 */
function paintSquareTableWithChairs(
  K: any,
  group: any,
  tx: number,
  ty: number,
  w: number,
  h: number,
  chairCount: number,
  diners: number,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  editSelected: boolean,
  attention: boolean,
  attentionTone: UppKonvaAttention,
): void {
  const cw = TABLE_CM.chairWidth * scalePxPerCm;
  const ch = TABLE_CM.chairHeight * scalePxPerCm;
  const margin = TABLE_CM.chairMargin * scalePxPerCm;
  const itemW = cw + margin;
  const paint = tableAttentionPaint(theme, attention, attentionTone);
  const strokeOuter = paint.outerStroke;
  const strokeInner = paint.chairInner;
  const strokeWidth = theme.elementStrokeWidth;

  const numChairs = allocateChairCountsPerSide(w, h, chairCount, cw, margin);
  const totalSlots = numChairs[0] + numChairs[1] + numChairs[2] + numChairs[3];
  const fillN = Math.min(Math.max(0, Math.trunc(diners)), totalSlots);
  const cwRank = rectTableCwRankByDrawIndexPerSide(numChairs);

  const chair: [number, number, number, number] = [0, 0, cw, ch];
  let drawIdx = 0;

  let rowW = numChairs[0] * itemW;
  chair[0] = tx + (itemW + w - rowW) / 2;
  chair[1] = ty;
  for (let c = 0; c < numChairs[0]; c++) {
    const filled = cwRank[drawIdx] !== undefined && cwRank[drawIdx] < fillN;
    drawSquareChair(
      K,
      group,
      0,
      chair[0],
      chair[1],
      chair[2],
      chair[3],
      strokeOuter,
      strokeInner,
      strokeWidth,
      filled,
    );
    chair[0] += itemW;
    drawIdx += 1;
  }

  rowW = numChairs[1] * itemW;
  chair[0] = tx + w;
  chair[1] = ty + (itemW + h - rowW) / 2;
  for (let c = 0; c < numChairs[1]; c++) {
    const filled = cwRank[drawIdx] !== undefined && cwRank[drawIdx] < fillN;
    drawSquareChair(
      K,
      group,
      90,
      chair[0],
      chair[1],
      chair[2],
      chair[3],
      strokeOuter,
      strokeInner,
      strokeWidth,
      filled,
    );
    chair[1] += itemW;
    drawIdx += 1;
  }

  rowW = numChairs[2] * itemW;
  chair[0] = tx + (itemW + w - rowW) / 2;
  chair[1] = ty + h;
  for (let c = 0; c < numChairs[2]; c++) {
    const filled = cwRank[drawIdx] !== undefined && cwRank[drawIdx] < fillN;
    drawSquareChair(
      K,
      group,
      180,
      chair[0],
      chair[1],
      chair[2],
      chair[3],
      strokeOuter,
      strokeInner,
      strokeWidth,
      filled,
    );
    chair[0] += itemW;
    drawIdx += 1;
  }

  rowW = numChairs[3] * itemW;
  chair[0] = tx;
  chair[1] = ty + (itemW + h - rowW) / 2;
  for (let c = 0; c < numChairs[3]; c++) {
    const filled = cwRank[drawIdx] !== undefined && cwRank[drawIdx] < fillN;
    drawSquareChair(
      K,
      group,
      90,
      chair[0],
      chair[1],
      chair[2],
      chair[3],
      strokeOuter,
      strokeInner,
      strokeWidth,
      filled,
    );
    chair[1] += itemW;
    drawIdx += 1;
  }

  const stroke = floorPlanInnerStrokeAttrs(theme, editSelected);
  group.add(
    new K.Rect({
      x: tx,
      y: ty,
      width: w,
      height: h,
      fill: theme.roomFill,
      stroke: strokeOuter,
      strokeWidth,
      listening: false,
    }),
  );
  group.add(
    new K.Rect({
      x: tx + 1,
      y: ty + 1,
      width: w - 2,
      height: h - 2,
      fill: paint.tabletopFill,
      stroke: stroke.stroke,
      strokeWidth: stroke.strokeWidth,
      listening: false,
    }),
  );
}

/**
 * Elliptic / round tabletop and chairs on the perimeter (v3 `_drawCircleTable`).
 * Local coords: bbox 0..w × 0..h, ellipse centred at (w/2, h/2) with semi-axes w/2, h/2.
 */
function paintEllipticTableWithChairs(
  K: any,
  group: any,
  w: number,
  h: number,
  chairCount: number,
  diners: number,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  editSelected: boolean,
  attention: boolean,
  attentionTone: UppKonvaAttention,
): void {
  const cw = TABLE_CM.chairWidth * scalePxPerCm;
  const ch = TABLE_CM.chairHeight * scalePxPerCm;
  const paint = tableAttentionPaint(theme, attention, attentionTone);
  const strokeOuter = paint.outerStroke;
  const strokeInner = paint.chairInner;
  const strokeWidth = theme.elementStrokeWidth;
  const rw = w / 2;
  const rh = h / 2;
  const cx = w / 2;
  const cy = h / 2;

  const size = Math.max(1, chairCount);
  const step = (2 * Math.PI) / size;
  const fillN = Math.min(Math.max(0, Math.trunc(diners)), size);
  const cwRank = roundTableCwRankByDrawIndex(size);

  for (let i = 0; i < size; i += 1) {
    const ag = i * step;
    const px = cx + rw * Math.cos(ag);
    const py = cy + rh * Math.sin(ag);
    const tanX = -rw * Math.sin(ag);
    const tanY = rh * Math.cos(ag);
    const angleDeg = (Math.atan2(tanY, tanX) * 180) / Math.PI;
    const filled = cwRank[i] !== undefined && cwRank[i] < fillN;
    drawSquareChair(K, group, angleDeg, px, py, cw, ch, strokeOuter, strokeInner, strokeWidth, filled);
  }

  const stroke = floorPlanInnerStrokeAttrs(theme, editSelected);
  group.add(
    new K.Ellipse({
      x: cx,
      y: cy,
      radiusX: rw,
      radiusY: rh,
      fill: theme.roomFill,
      stroke: strokeOuter,
      strokeWidth: theme.elementStrokeWidth,
      listening: false,
    }),
  );
  group.add(
    new K.Ellipse({
      x: cx,
      y: cy,
      radiusX: Math.max(1, rw - 1),
      radiusY: Math.max(1, rh - 1),
      fill: paint.tabletopFill,
      stroke: stroke.stroke,
      strokeWidth: stroke.strokeWidth,
      listening: false,
    }),
  );
}

/** Elliptic tabletop only (`chairCount === 0`). */
function paintEllipticTableTopOnly(
  K: any,
  group: any,
  w: number,
  h: number,
  theme: UppFloorPlanTheme,
  editSelected: boolean,
  attention: boolean,
  attentionTone: UppKonvaAttention,
): void {
  const cx = w / 2;
  const cy = h / 2;
  const rw = w / 2;
  const rh = h / 2;
  const paint = tableAttentionPaint(theme, attention, attentionTone);
  const strokeOuter = paint.outerStroke;
  const stroke = floorPlanInnerStrokeAttrs(theme, editSelected);
  group.add(
    new K.Ellipse({
      x: cx,
      y: cy,
      radiusX: rw,
      radiusY: rh,
      fill: theme.roomFill,
      stroke: strokeOuter,
      strokeWidth: theme.elementStrokeWidth,
      listening: false,
    }),
  );
  group.add(
    new K.Ellipse({
      x: cx,
      y: cy,
      radiusX: Math.max(1, rw - 1),
      radiusY: Math.max(1, rh - 1),
      fill: paint.tabletopFill,
      stroke: stroke.stroke,
      strokeWidth: stroke.strokeWidth,
      listening: false,
    }),
  );
}

/** Plain rectangle table (no chairs), used when `chairCount === 0`. */
export function createFloorPlanTableRect(
  K: any,
  xPx: number,
  yPx: number,
  widthPx: number,
  heightPx: number,
  rotationDeg: number,
  theme: UppFloorPlanTheme,
  selected: boolean,
  listening: boolean,
  editSelected = false,
  elementId?: string,
  attention = false,
  attentionTone: UppKonvaAttention = 'WARNING',
): any {
  const stroke =
    editSelected && selected
      ? { stroke: '#dc2626' as const, strokeWidth: theme.roomStrokeWidth + 1.5 }
      : floorPlanInnerStrokeAttrs(theme, false);
  const fill = tableAttentionPaint(theme, attention, attentionTone).tabletopFill;
  const r = new K.Rect({
    x: xPx,
    y: yPx,
    width: widthPx,
    height: heightPx,
    fill,
    ...stroke,
    rotation: rotationDeg,
    listening,
  });
  if (elementId) {
    r.id(elementId);
  }
  return r;
}

/**
 * Group centered on table bbox (v3-style rotation), chairs + square table, hit rect matches logical table area.
 */
export function createFloorPlanTableNode(
  K: any,
  el: UppFloorPlanTable,
  xPx: number,
  yPx: number,
  widthPx: number,
  heightPx: number,
  rotationDeg: number,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  selected: boolean,
  listening: boolean,
  editSelected = false,
): any {
  const attention = el.options.attention === true;
  const attentionTone = el.attentionTone;
  const diners = Math.max(0, Math.trunc(Number.isFinite(el.diners) ? el.diners : 0));
  if (el.chairCount <= 0 && el.tableShape === 'RECT') {
    return createFloorPlanTableRect(
      K,
      xPx,
      yPx,
      widthPx,
      heightPx,
      rotationDeg,
      theme,
      selected,
      listening,
      editSelected,
      el.uuid,
      attention,
      attentionTone,
    );
  }

  const w = widthPx;
  const h = heightPx;
  /** Top-left bbox in room px, rotation around center (Konva `offset`); matches `syncKonvaNodeGeometryToElement`. */
  const g = new K.Group({
    id: el.uuid,
    x: xPx + w / 2,
    y: yPx + h / 2,
    offsetX: w / 2,
    offsetY: h / 2,
    width: w,
    height: h,
    rotation: rotationDeg,
  });

  if (el.tableShape === 'ELLIPSE') {
    if (el.chairCount > 0) {
      paintEllipticTableWithChairs(
        K,
        g,
        w,
        h,
        el.chairCount,
        diners,
        scalePxPerCm,
        theme,
        editSelected,
        attention,
        attentionTone,
      );
    } else {
      paintEllipticTableTopOnly(K, g, w, h, theme, editSelected, attention, attentionTone);
    }
  } else {
    paintSquareTableWithChairs(
      K,
      g,
      0,
      0,
      w,
      h,
      el.chairCount,
      diners,
      scalePxPerCm,
      theme,
      editSelected,
      attention,
      attentionTone,
    );
  }
  addFloorPlanHitRect(K, g, w, h, theme, selected, listening, 0, 0, editSelected);
  g.listening(listening);
  return g;
}
